import { PartlistColumn } from "@org-avp/avp-avengers-partlist-service-api";
import { ColumnParams, CommonColumnParamsState, dndHandleColumnId } from "@org-avp/avp-avengers-ui-shared-utils";
import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { ColumnPinnedType } from "ag-grid-community";
import { GetStateFn } from "../../utils/dispatchUtils";

type GridFilterModel = { [p: string]: unknown };

export const partlistViewOptionsSliceName = "partlist";
export const partlistColumnIds = [
  dndHandleColumnId,
  PartlistColumn.USAGE,
  PartlistColumn.GEOMETRY_BUILD_UP_STATE,
  PartlistColumn.BOM_FILTER_CLASS,
  PartlistColumn.BOM_PART_KEY,
  PartlistColumn.BOM_DESCRIPTION,
  PartlistColumn.NAME_SUFFIX,
  PartlistColumn.BOM_IS_COLOR_RELEVANT,
  PartlistColumn.BOM_INCLUSION_DATE,
  PartlistColumn.BOM_EXCLUSION_DATE,
  PartlistColumn.SUMMARY,
  PartlistColumn.BOM_ORIGINAL_RULE,
  PartlistColumn.MARKETING_RULES,
  PartlistColumn.MARKETING_TRIGGERS,
  PartlistColumn.GEOMETRY_DOCUMENT,
  PartlistColumn.GEOMETRY_SYNC_STATE,
  PartlistColumn.GEOMETRY_KVS_DOCUMENT,
  PartlistColumn.MARKETING_CONDITIONS,
  PartlistColumn.BOM_ZSB,
  PartlistColumn.BOM_INCLUSION_KEY,
  PartlistColumn.BOM_EXCLUSION_KEY,
  PartlistColumn.BOM_LOCATION_VARIANT,
  PartlistColumn.BOM_QUANTITY,
  PartlistColumn.BOM_QUANTITY_UNIT,
  PartlistColumn.GEOMETRY_BRAND,
  PartlistColumn.MASTER_SHEET,
  PartlistColumn.BOM_KIT_FLAG,
  PartlistColumn.CREATED_AT,
  PartlistColumn.LAST_CHANGE_AT,
  PartlistColumn.MARKETING_POSITIONS,
  PartlistColumn.COMMENT,
  PartlistColumn.SOURCE,
  PartlistColumn.BOM_NODE_NAME,
  PartlistColumn.BOM_NODE_DESCRIPTION,
  PartlistColumn.BOM_SEQUENCE,
  PartlistColumn.BOM_BASE_NUMBER,
  PartlistColumn.BOM_PARTLIST_POSITION_NUMBER,
  PartlistColumn.BOM_SUCCESSOR,
  PartlistColumn.BOM_PREDECESSOR,
  // FVA
  PartlistColumn.BOM_ORG_PART_NUMBER,
  PartlistColumn.BOM_ORG_PART_NAME,
  PartlistColumn.BOM_ITEM_ID,
  PartlistColumn.BOM_M_STATUS,
  PartlistColumn.BOM_ORG_SUCCESSOR,
];
export type PartlistViewOptionsState = CommonColumnParamsState & {
  filterModel: GridFilterModel;
};

const persistedPartlistViewOptions = localStorage.getItem(partlistViewOptionsSliceName);
const persistedState = persistedPartlistViewOptions ? JSON.parse(persistedPartlistViewOptions) as PartlistViewOptionsState : null;
const fallback: PartlistViewOptionsState = {
  columns: partlistColumnIds,
  params: Object.fromEntries(partlistColumnIds.map((columnId) => [columnId, { columnId: columnId }])),
  filterModel: {},
};
const initialState: PartlistViewOptionsState = persistedState ?? fallback;
// if columns does not exist, set default fallback value
if (initialState.columns === undefined || initialState.columns.length === 0)
  initialState.columns = fallback.columns;

if (initialState.params === undefined)
  initialState.params = fallback.params;

const partlistViewOptionsSlice = createSlice({
  name: partlistViewOptionsSliceName,
  initialState,
  reducers: {
    setLayoutProfileString(state: PartlistViewOptionsState, action: PayloadAction<string>) {
      const parsedProfileSettings = JSON.parse(action.payload);
      state.params = parsedProfileSettings.params;
      state.columns = parsedProfileSettings.columns;
    },
    setPartlistViewColumns(state: PartlistViewOptionsState, action: PayloadAction<string[]>) {
      state.columns = action.payload;
    },
    updateColumnWidth(state: PartlistViewOptionsState, action: PayloadAction<ColumnParams>) {
      const params = state.params ?? {};
      params[action.payload.columnId].width = action.payload.width;
      state.params = params;
    },
    updateColumnPinned(state: PartlistViewOptionsState, action: PayloadAction<ColumnParams>) {
      const params = state.params ?? {};
      params[action.payload.columnId].pinned = action.payload.pinned;
      state.params = params;
    },
    setPartlistViewFilterModel(state: PartlistViewOptionsState, action: PayloadAction<GridFilterModel>) {
      state.filterModel = action.payload;
    },
  },
});

export type PartlistViewOptionsRootState = { [partlistViewOptionsSliceName]: PartlistViewOptionsState };
export const selectPartlistViewOptions = (state: PartlistViewOptionsRootState) => state[partlistViewOptionsSliceName];
export const selectPartlistLayoutProfileString = (state: PartlistViewOptionsRootState) => {
  const stateElement = state[partlistViewOptionsSliceName];
  // save without filters
  return JSON.stringify({ params: stateElement.params, columns: stateElement.columns });
};
export const setPartlistLayoutProfileString = (layoutProfileString: string) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(partlistViewOptionsSlice.actions.setLayoutProfileString(layoutProfileString));
  persistState(getState);
};

export const setPartlistViewColumns = (columns: string[]) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(partlistViewOptionsSlice.actions.setPartlistViewColumns(columns));
  persistState(getState);
};
export const updatePartlistColumnWidth = (columnId: string, columnWidth?: number) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(partlistViewOptionsSlice.actions.updateColumnWidth({ columnId, width: columnWidth }));
  persistState(getState);
};
export const updatePartlistColumnPinned = (columnId: string, columnPinned?: ColumnPinnedType) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(partlistViewOptionsSlice.actions.updateColumnPinned({ columnId, pinned: columnPinned }));
  persistState(getState);
};
export const setPartlistViewFilterModel = (filterModel: GridFilterModel) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(partlistViewOptionsSlice.actions.setPartlistViewFilterModel(filterModel));
  persistState(getState);
};

function persistState(getState: GetStateFn) {
  localStorage.setItem(partlistViewOptionsSliceName, JSON.stringify(
    selectPartlistViewOptions(getState()),
  ));
}

export const partlistViewOptionsReducer = partlistViewOptionsSlice.reducer;
